export type HighwayCategory =
  | 'bike'
  | 'path'
  | 'steps'
  | 'quiet'
  | 'arterial'
  | 'unknown'
  | 'default'

const CATEGORY_MATCHERS: Array<{ category: HighwayCategory; keywords: string[] }> = [
  { category: 'bike', keywords: ['cycle', 'bike', 'cycleway'] },
  { category: 'path', keywords: ['footway', 'foot', 'path', 'trail', 'bridleway'] },
  { category: 'steps', keywords: ['steps'] },
  { category: 'quiet', keywords: ['service', 'residential', 'living', 'tertiary', 'unclassified'] },
  { category: 'arterial', keywords: ['primary', 'secondary', 'motorway', 'trunk'] }
]

const CATEGORY_COLORS: Record<HighwayCategory, string> = {
  bike: '#22c55e',
  path: '#14b8a6',
  steps: '#a855f7',
  quiet: '#f59e0b',
  arterial: '#ef4444',
  unknown: '#9ca3af',
  default: '#6b7280'
}

const CATEGORY_LABELS: Record<Exclude<HighwayCategory, 'default'>, { label: string; description: string }> = {
  bike: {
    label: 'Bike Infrastructure',
    description: 'Cycleways, protected bike lanes'
  },
  path: {
    label: 'Shared Paths & Trails',
    description: 'Multi-use paths, footways, rail trails'
  },
  steps: {
    label: 'Stairs / Not Rideable',
    description: 'Steps or steep staircases'
  },
  quiet: {
    label: 'Local & Quiet Streets',
    description: 'Residential, service and calmer roads'
  },
  arterial: {
    label: 'Heavy Traffic Roads',
    description: 'Primary, secondary, trunk or motorways'
  },
  unknown: {
    label: 'Unknown Classification',
    description: 'Segments without highway metadata'
  }
}

export function getHighwayCategory(type?: string | null): HighwayCategory {
  if (!type || !type.trim()) {
    return 'unknown'
  }

  const normalized = type.toLowerCase()

  for (const { category, keywords } of CATEGORY_MATCHERS) {
    if (keywords.some(keyword => normalized.includes(keyword))) {
      return category
    }
  }

  if (normalized === 'unknown') {
    return 'unknown'
  }

  return 'default'
}

export function getHighwayColor(type?: string | null): string {
  const category = getHighwayCategory(type)
  return CATEGORY_COLORS[category]
}

export function getHighwayCategoryLabel(category: HighwayCategory): { label: string; description: string } | null {
  if (category === 'default') {
    return null
  }
  return CATEGORY_LABELS[category]
}

export const HIGHWAY_LEGEND_ORDER: HighwayCategory[] = [
  'bike',
  'path',
  'quiet',
  'arterial',
  'steps',
  'unknown'
]

export const HIGHWAY_CATEGORY_CONFIG = {
  colors: CATEGORY_COLORS,
  labels: CATEGORY_LABELS
}
